import { useState } from 'react'
import { Search, ChevronDown, LogOut, User as UserIcon, Sparkles } from 'lucide-react'
import { useAuth } from '../auth/AuthContext'
import { Avatar, Button, Menu, Tooltip } from '../design'
import { AgentChat } from '../views/AgentChat'

type Crumb = { label: string; href?: string }

export function Topbar({ breadcrumbs = [], right, projectId }: { breadcrumbs?: Crumb[]; right?: React.ReactNode; projectId?: number }) {
  const { user, logout } = useAuth()
  const [agentOpen, setAgentOpen] = useState(false)

  return (
    <header className="flex h-14 items-center justify-between border-b border-border bg-bg-elevated px-5">
      <div className="flex items-center gap-3 text-sm">
        {breadcrumbs.length === 0 ? (
          <span className="text-fg-subtle">Atlas</span>
        ) : (
          breadcrumbs.map((c, i) => (
            <div key={i} className="flex items-center gap-2">
              {i > 0 && <span className="text-fg-disabled">/</span>}
              {c.href ? (
                <a href={c.href} className="text-fg-muted hover:text-fg">
                  {c.label}
                </a>
              ) : (
                <span className="text-fg font-medium">{c.label}</span>
              )}
            </div>
          ))
        )}
      </div>

      <div className="flex items-center gap-3">
        <button
          type="button"
          className="hidden md:inline-flex items-center gap-2 h-8 px-2.5 rounded-md border border-border bg-bg text-fg-subtle text-xs hover:text-fg hover:bg-bg-muted"
        >
          <Search size={14} />
          <span>Search…</span>
          <kbd className="ml-3 font-mono text-[10px] text-fg-disabled">⌘K</kbd>
        </button>

        <Tooltip content="Open Planning Agent">
          <button
            type="button"
            onClick={() => setAgentOpen(true)}
            className="h-8 w-8 inline-flex items-center justify-center rounded-md border border-border bg-bg-elevated text-accent hover:bg-accent-soft transition-colors"
          >
            <Sparkles size={14} />
          </button>
        </Tooltip>

        {right}

        <Menu
          align="end"
          trigger={
            <Button variant="ghost" size="md" className="-mr-2 gap-2">
              <Avatar size="sm" name={user?.name} color={user?.avatar_color ?? '#d97757'} />
              <span className="hidden sm:inline text-sm font-medium">{user?.name}</span>
              <ChevronDown size={14} className="text-fg-subtle" />
            </Button>
          }
          items={[
            { kind: 'label', label: user?.email ?? '' },
            {
              kind: 'item',
              label: (
                <span className="flex items-center gap-2">
                  <UserIcon size={14} /> Settings
                </span>
              ),
              onSelect: () => { window.location.href = '/settings' },
            },
            { kind: 'separator' },
            {
              kind: 'item',
              label: (
                <span className="flex items-center gap-2">
                  <LogOut size={14} /> Sign out
                </span>
              ),
              onSelect: () => { void logout() },
              danger: true,
            },
          ]}
        />
      </div>

      <AgentChat open={agentOpen} onOpenChange={setAgentOpen} projectId={projectId} />
    </header>
  )
}
